import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MongoDbIcon from '@mui/icons-material/DataObject';
import HistoryIcon from '@mui/icons-material/History';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SpeedIcon from '@mui/icons-material/Speed';
import StorageIcon from '@mui/icons-material/Storage';
import VideoCameraBackIcon from '@mui/icons-material/VideoCameraBack';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import VideoSettingsIcon from '@mui/icons-material/VideoSettings';
// Import icons for collapse functionality
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  CircularProgress,
  Collapse,
  Divider,
  Fab,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  styled,
  Switch,
  Tooltip,
  Typography,
  Zoom
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import fileSystemAccessService from '../services/FileSystemAccessService';

// Custom styled switch with MongoDB colors
const ExperienceSwitch = styled(Switch)(({ theme }) => ({
  width: 62,
  height: 34,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    transform: 'translateX(6px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translateX(22px)',
      '& .MuiSwitch-thumb': {
        backgroundColor: '#13AA52', // MongoDB green
      },
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#C2E0C1', // Light green
      },
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: theme.palette.primary.main,
    width: 32,
    height: 32,
    '&:before': {
      content: "''",
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    },
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#E9E9EA',
    borderRadius: 20 / 2,
  },
}));

// Custom styled switch for video experience with purple theme
const VideoSwitch = styled(Switch)(({ theme }) => ({
  width: 62,
  height: 34,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    transform: 'translateX(6px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translateX(22px)',
      '& .MuiSwitch-thumb': {
        backgroundColor: '#6200EA', // Deep purple
      },
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#D1C4E9', // Light purple
      },
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: theme.palette.primary.main,
    width: 32,
    height: 32,
    '&:before': {
      content: "''",
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    },
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#E9E9EA',
    borderRadius: 20 / 2,
  },
}));

// Custom styled switch for user experience with blue theme
const UserSwitch = styled(Switch)(({ theme }) => ({
  width: 62,
  height: 34,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    transform: 'translateX(6px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translateX(22px)',
      '& .MuiSwitch-thumb': {
        backgroundColor: '#0277BD', // Blue
      },
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#BBDEFB', // Light blue
      },
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: theme.palette.primary.main,
    width: 32,
    height: 32,
    '&:before': {
      content: "''",
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    },
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#E9E9EA',
    borderRadius: 20 / 2,
  },
}));

const ExperienceSwitcher = () => {
  // Add state for collapse functionality
  const [expanded, setExpanded] = useState(false);
  
  // Initialize states from localStorage via the service
  const [apiVersion, setApiVersion] = useState(() => {
    // Check if the user has saved a preference already
    const savedVersion = fileSystemAccessService.apiVersion;
    // If nothing is saved (new user), default to v2 (MongoDB)
    return savedVersion || 'v2';
  });

  const [videoExperience, setVideoExperience] = useState(() => {
    // Check if the user has saved a preference already
    const savedExperience = fileSystemAccessService.getVideoExperience?.();
    // If nothing is saved (new user), default to 'new'
    return savedExperience || 'new';
  });

  const [userExperience, setUserExperience] = useState(() => {
    // Check if the user has saved a preference already 
    const savedExperience = fileSystemAccessService.getUserExperience?.();
    // If nothing is saved (new user), default to 'new'
    return savedExperience || 'new';
  });

  const [loading, setLoading] = useState(false);
  const [versionAvailability, setVersionAvailability] = useState({
    v1: true,  // Assume all are available initially
    v2: true,
    videoClassic: true,
    videoNew: true,
    userClassic: true,
    userNew: true
  });

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    // Check availability but don't auto-switch versions
    checkVersionAvailability();
  }, []);

  // Check if both versions are available but don't auto-switch
  const checkVersionAvailability = async () => {
    setLoading(true);
    
    try {
      // Check both API versions
      const v1Available = await fileSystemAccessService.testApiVersion('v1');
      const v2Available = await fileSystemAccessService.testApiVersion('v2');
      
      // Check both video experiences
      const videoClassicAvailable = true;
      const videoNewAvailable = true;
      
      // Check both user experiences
      const userClassicAvailable = true;
      const userNewAvailable = true;
      
      setVersionAvailability({
        v1: v1Available,
        v2: v2Available,
        videoClassic: videoClassicAvailable,
        videoNew: videoNewAvailable,
        userClassic: userClassicAvailable,
        userNew: userNewAvailable
      });
      
      // If current version is not available, show warning but don't auto-switch
      if (apiVersion === 'v1' && !v1Available) {
        console.warn('Selected SQL database is not available');
      } else if (apiVersion === 'v2' && !v2Available) {
        console.warn('Selected MongoDB database is not available');
      }
      
      // Check if current video experience is available
      if (videoExperience === 'classic' && !videoClassicAvailable) {
        console.warn('Classic video experience is not available');
      } else if (videoExperience === 'new' && !videoNewAvailable) {
        console.warn('New video experience is not available');
      }
      
      // Check if current user experience is available
      if (userExperience === 'classic' && !userClassicAvailable) {
        console.warn('Classic user experience is not available');
      } else if (userExperience === 'new' && !userNewAvailable) {
        console.warn('Enhanced user experience is not available');
      }
    } catch (error) {
      console.error('Error checking available versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDatabaseSwitchChange = (event) => {
    const newVersion = event.target.checked ? 'v2' : 'v1';
    console.log('Database experience mode changed to:', newVersion);
    
    // Check if the selected version is available
    if ((newVersion === 'v1' && !versionAvailability.v1) || 
        (newVersion === 'v2' && !versionAvailability.v2)) {
      console.warn(`${newVersion === 'v1' ? 'SQL' : 'MongoDB'} database is not available`);
      // Still save the preference but warn the user
    }
    
    // Always save the user preference regardless of availability
    const success = fileSystemAccessService.setApiVersion(newVersion);
    if (success) {
      setApiVersion(newVersion);
    }
  };

  const handleVideoSwitchChange = (event) => {
    const newExperience = event.target.checked ? 'new' : 'classic';
    console.log('Video experience mode changed to:', newExperience);
    
    // Check if the selected experience is available
    if ((newExperience === 'classic' && !versionAvailability.videoClassic) || 
        (newExperience === 'new' && !versionAvailability.videoNew)) {
      console.warn(`${newExperience === 'classic' ? 'Classic' : 'New'} video experience is not available`);
      // Still save the preference but warn the user
    }
    
    // Save the user preference
    const success = fileSystemAccessService.setVideoExperience(newExperience);
    if (success) {
      setVideoExperience(newExperience);
      
      // Dispatch a custom event to notify other components
      window.dispatchEvent(new CustomEvent('videoExperienceChanged'));
    }
  };

  const handleUserSwitchChange = (event) => {
    const newExperience = event.target.checked ? 'new' : 'classic';
    console.log('User experience mode changed to:', newExperience);
    
    // Check if the selected experience is available
    if ((newExperience === 'classic' && !versionAvailability.userClassic) || 
        (newExperience === 'new' && !versionAvailability.userNew)) {
      console.warn(`${newExperience === 'classic' ? 'Classic' : 'Enhanced'} user experience is not available`);
      // Still save the preference but warn the user
    }
    
    // Save the user preference
    const success = fileSystemAccessService.setUserExperience?.(newExperience) ?? true;
    if (success) {
      setUserExperience(newExperience);
      
      // Dispatch a custom event to notify other components
      window.dispatchEvent(new CustomEvent('userExperienceChanged'));
    }
  };

  // Generate status text if current selection is unavailable
  const getStatusText = () => {
    const messages = [];
    
    if (apiVersion === 'v1' && !versionAvailability.v1) {
      messages.push("SQL database unavailable");
    } else if (apiVersion === 'v2' && !versionAvailability.v2) {
      messages.push("MongoDB database unavailable");
    }
    
    if (videoExperience === 'classic' && !versionAvailability.videoClassic) {
      messages.push("Classic video experience unavailable");
    } else if (videoExperience === 'new' && !versionAvailability.videoNew) {
      messages.push("New video experience unavailable");
    }
    
    if (userExperience === 'classic' && !versionAvailability.userClassic) {
      messages.push("Classic user experience unavailable");
    } else if (userExperience === 'new' && !versionAvailability.userNew) {
      messages.push("Enhanced user experience unavailable");
    }
    
    return messages.length > 0 ? messages.join(", ") : null;
  };

  const statusText = getStatusText();

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
    >
      {/* Main collapsible panel */}
      <Collapse in={expanded} sx={{ mb: 2 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 2,
            borderRadius: 2,
            maxWidth: '300px',
            backgroundColor: theme => theme.palette.background.paper,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center' }}>
              <TuneIcon sx={{ mr: 1 }} fontSize="small" />
              Experience Settings
            </Typography>
            <IconButton size="small" onClick={toggleExpanded} sx={{ ml: 1 }}>
              <ExpandMoreIcon />
            </IconButton>
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={20} thickness={5} sx={{ mr: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Checking availability...
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {/* Database Experience Switch */}
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ ml: 1 }}>
                  Database Engine
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title={!versionAvailability.v1 ? "SQL database is currently unavailable" : "Classic experience with proven reliability"}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center', 
                      minWidth: '70px',
                      opacity: !versionAvailability.v1 ? 0.6 : 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                        <StorageIcon 
                          fontSize="small" 
                          color={apiVersion === 'v1' ? (!versionAvailability.v1 ? 'error' : 'primary') : 'disabled'} 
                          sx={{ mr: 0.5 }} 
                        />
                        <HistoryIcon 
                          fontSize="small" 
                          color={apiVersion === 'v1' ? (!versionAvailability.v1 ? 'error' : 'primary') : 'disabled'} 
                          sx={{ fontSize: '0.9rem' }} 
                        />
                      </Box>
                      <Typography 
                        variant="caption" 
                        color={apiVersion === 'v1' ? (!versionAvailability.v1 ? 'error' : 'primary') : 'text.secondary'}
                        sx={{ fontWeight: apiVersion === 'v1' ? 500 : 400 }}
                      >
                        Classic DB
                      </Typography>
                    </Box>
                  </Tooltip>
                  
                  <FormControlLabel
                    control={
                      <ExperienceSwitch 
                        checked={apiVersion === 'v2'} 
                        onChange={handleDatabaseSwitchChange}
                      />
                    }
                    label=""
                  />
                  
                  <Tooltip title={!versionAvailability.v2 ? "MongoDB is currently unavailable" : "Enhanced experience with faster performance"}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: '70px',
                      opacity: !versionAvailability.v2 ? 0.6 : 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                        <MongoDbIcon 
                          fontSize="small" 
                          color={apiVersion === 'v2' ? (!versionAvailability.v2 ? 'error' : 'success') : 'disabled'} 
                          sx={{ mr: 0.5 }} 
                        />
                        <SpeedIcon 
                          fontSize="small" 
                          color={apiVersion === 'v2' ? (!versionAvailability.v2 ? 'error' : 'success') : 'disabled'} 
                          sx={{ fontSize: '0.9rem' }} 
                        />
                      </Box>
                      <Typography 
                        variant="caption" 
                        color={apiVersion === 'v2' ? (!versionAvailability.v2 ? 'error' : 'success.main') : 'text.secondary'}
                        sx={{ fontWeight: apiVersion === 'v2' ? 500 : 400 }}
                      >
                        Enhanced DB
                      </Typography>
                    </Box>
                  </Tooltip>
                </Stack>
              </Stack>
              
              <Divider flexItem />
              
              {/* Video Experience Switch */}
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ ml: 1 }}>
                  Video Experience
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title={!versionAvailability.videoClassic ? "Classic video experience is currently unavailable" : "Traditional video processing workflow"}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center', 
                      minWidth: '70px',
                      opacity: !versionAvailability.videoClassic ? 0.6 : 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                        <VideoLibraryIcon 
                          fontSize="small" 
                          color={videoExperience === 'classic' ? (!versionAvailability.videoClassic ? 'error' : 'primary') : 'disabled'} 
                          sx={{ mr: 0.5 }} 
                        />
                        <VideoCameraBackIcon 
                          fontSize="small" 
                          color={videoExperience === 'classic' ? (!versionAvailability.videoClassic ? 'error' : 'primary') : 'disabled'} 
                          sx={{ fontSize: '0.9rem' }} 
                        />
                      </Box>
                      <Typography 
                        variant="caption" 
                        color={videoExperience === 'classic' ? (!versionAvailability.videoClassic ? 'error' : 'primary') : 'text.secondary'}
                        sx={{ fontWeight: videoExperience === 'classic' ? 500 : 400 }}
                      >
                        Classic Video
                      </Typography>
                    </Box>
                  </Tooltip>
                  
                  <FormControlLabel
                    control={
                      <VideoSwitch 
                        checked={videoExperience === 'new'} 
                        onChange={handleVideoSwitchChange}
                      />
                    }
                    label=""
                  />
                  
                  <Tooltip title={!versionAvailability.videoNew ? "New video experience is currently unavailable" : "Enhanced video processing with improved performance"}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: '70px',
                      opacity: !versionAvailability.videoNew ? 0.6 : 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                        <VideoSettingsIcon 
                          fontSize="small" 
                          color={videoExperience === 'new' ? (!versionAvailability.videoNew ? 'error' : 'secondary') : 'disabled'} 
                          sx={{ mr: 0.5 }} 
                        />
                        <NewReleasesIcon 
                          fontSize="small" 
                          color={videoExperience === 'new' ? (!versionAvailability.videoNew ? 'error' : 'secondary') : 'disabled'} 
                          sx={{ fontSize: '0.9rem' }} 
                        />
                      </Box>
                      <Typography 
                        variant="caption" 
                        color={videoExperience === 'new' ? (!versionAvailability.videoNew ? 'error' : 'secondary.main') : 'text.secondary'}
                        sx={{ fontWeight: videoExperience === 'new' ? 500 : 400 }}
                      >
                        Enhanced Video
                      </Typography>
                    </Box>
                  </Tooltip>
                </Stack>
              </Stack>
              
              <Divider flexItem />
              
              {/* User Experience Switch */}
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ ml: 1 }}>
                  User Management
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title={!versionAvailability.userClassic ? "Classic user experience is currently unavailable" : "Traditional user management"}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center', 
                      minWidth: '70px',
                      opacity: !versionAvailability.userClassic ? 0.6 : 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                        <PersonIcon 
                          fontSize="small" 
                          color={userExperience === 'classic' ? (!versionAvailability.userClassic ? 'error' : 'primary') : 'disabled'} 
                          sx={{ mr: 0.5 }} 
                        />
                        <PersonAddIcon 
                          fontSize="small" 
                          color={userExperience === 'classic' ? (!versionAvailability.userClassic ? 'error' : 'primary') : 'disabled'} 
                          sx={{ fontSize: '0.9rem' }} 
                        />
                      </Box>
                      <Typography 
                        variant="caption" 
                        color={userExperience === 'classic' ? (!versionAvailability.userClassic ? 'error' : 'primary') : 'text.secondary'}
                        sx={{ fontWeight: userExperience === 'classic' ? 500 : 400 }}
                      >
                        Classic User
                      </Typography>
                    </Box>
                  </Tooltip>
                  
                  <FormControlLabel
                    control={
                      <UserSwitch 
                        checked={userExperience === 'new'} 
                        onChange={handleUserSwitchChange}
                      />
                    }
                    label=""
                  />
                  
                  <Tooltip title={!versionAvailability.userNew ? "Enhanced user experience is currently unavailable" : "Advanced user management with role-based access control"}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: '70px',
                      opacity: !versionAvailability.userNew ? 0.6 : 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                        <ManageAccountsIcon 
                          fontSize="small" 
                          color={userExperience === 'new' ? (!versionAvailability.userNew ? 'error' : 'info') : 'disabled'} 
                          sx={{ mr: 0.5 }} 
                        />
                        <AdminPanelSettingsIcon 
                          fontSize="small" 
                          color={userExperience === 'new' ? (!versionAvailability.userNew ? 'error' : 'info') : 'disabled'} 
                          sx={{ fontSize: '0.9rem' }} 
                        />
                      </Box>
                      <Typography 
                        variant="caption" 
                        color={userExperience === 'new' ? (!versionAvailability.userNew ? 'error' : 'info.main') : 'text.secondary'}
                        sx={{ fontWeight: userExperience === 'new' ? 500 : 400 }}
                      >
                        Enhanced User
                      </Typography>
                    </Box>
                  </Tooltip>
                </Stack>
              </Stack>
            </Stack>
          )}
          
          {/* Show status message if any current selection is unavailable */}
          {statusText && !loading && (
            <Typography 
              variant="caption" 
              color="error" 
              sx={{ mt: 2, display: 'block', fontSize: '0.7rem', fontStyle: 'italic' }}
            >
              {statusText}
            </Typography>
          )}
        </Paper>
      </Collapse>
      
      {/* Toggle button */}
      <Zoom in={!expanded}>
        <Fab 
          color="primary" 
          size="medium" 
          onClick={toggleExpanded} 
          aria-label="experience settings"
          sx={{ boxShadow: 3 }}
        >
          <SettingsIcon />
        </Fab>
      </Zoom>
      
      {/* Small indicator when collapsed */}
      <Zoom in={!expanded}>
        <Paper 
          elevation={1}
          sx={{
            position: 'absolute',
            bottom: -8,
            right: 55,
            py: 0.5,
            px: 1,
            borderRadius: 5,
            backgroundColor: apiVersion === 'v2' ? 'success.light' : 'primary.light',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500, color: 'white' }}>
            {apiVersion === 'v2' ? 'MongoDB' : 'SQL'}
          </Typography>
        </Paper>
      </Zoom>
    </Box>
  );
};

export default ExperienceSwitcher;